import * as React from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import ChatPanel from "@carousel-office/shared/components/chat/ChatPanel";
import { ConversationHistoryItem } from "@carousel-office/shared/components/chat";
import { ConversationProvider, useConversation } from "@carousel-office/shared/context/ConversationContext";
import type { Message } from "@carousel-office/shared/context/ConversationContext";
import { initializeMessageService } from "../hooks/useSendMessage";
import {
  authService,
  createTaskpaneAppDialogOpeners,
  InitializingScreen,
  backendSettingsService,
  type BackendCustomInstructions,
} from "@carousel-office/shared";
import { generateConversationId } from "../../main/utils/idGenerator";
import { createTaskpaneChatConversation } from "../services/taskpaneChatService";
import { DebugPanel } from "./DebugPanel";
import { ToolTestPanel, setDevModeEnabled } from "./ToolTestPanel";
import { useModelSettings } from "../hooks/useSettings";
import { promptLibraryService, SavedPrompt } from "../../services/promptLibraryService";
import { mentionFilesService } from "../../services/mentionFilesService";
import { fetchConversations } from "../services/conversationFetchService";
import { restoreInMemoryConversation, restorePersistedConversation } from "../services/conversationRestoreService";
import { rewriteConversationFromUserTurn } from "../services/conversationEditService";
import { getDeckRegistry } from "../../services/tracking/deckRegistry";
import { LiveTrackingStorage } from "../../services/tracking/liveTrackingStorage";
import { settingsService } from "../../services/settingsService";
import GlobalStateManager from "../../main/globals/global-state/globalStateManager";
import { configureAgentServices } from "@carousel-office/agent-runtime";
import { getContextResolver } from "../../main/core/contextResolver";
import { getReminderCollector, getMessagePreparer } from "../../main/globals/llm/reminder";
import { getMessageBuilder } from "../../main/globals/llm/messageBuilder";
import { getSessionLogger } from "../../main/services/logging";
import { externalFilesCache } from "../../services/externalFilesCache";
import { backendClient } from "../../services/communication/backendClient";
import { getCurrentPresentationContext } from "../../main/utils/presentationContext";
import { getCheckpointManager } from "../../main/services/logging/checkpointManager";
import { isMutatingToolName } from "../../main/services/logging/checkpointMutability";
import { collectDeckRegistryContext, collectPresentationOverview } from "../../main/globals/llm/reminder/pptContextProviders";

const getReminderPresentationContext = async () => {
  const context = await getCurrentPresentationContext();
  if (!context) {
    return undefined;
  }

  // Agent runtime reminders expect a `slides` array; build a lightweight version
  // from the current presentation context to avoid shape mismatch crashes.
  const slides = Array.isArray(context.allSlideIds)
    ? context.allSlideIds.map((slideId, index) => ({
        slideId,
        slideIndex: index + 1,
      }))
    : [];

  return {
    documentId: context.documentId,
    filename: context.presentationTitle,
    totalSlides: context.totalSlides,
    currentSlideIndex: context.currentSlideIndex ?? undefined,
    slides,
    selectedShapeIds: [] as string[],
  };
};

const REMINDER_CATEGORIES: Array<keyof BackendCustomInstructions> = [
  "carouselChat",
  "search",
  "research",
  "execution",
];

function formatBackendInstructionsForCategory(
  instructions: BackendCustomInstructions | null,
  category: string
): string {
  const normalizedCategory = category as keyof BackendCustomInstructions;
  if (!REMINDER_CATEGORIES.includes(normalizedCategory) || !instructions) {
    return "";
  }
  const entries = Array.isArray(instructions[normalizedCategory])
    ? instructions[normalizedCategory]
    : [];
  return entries
    .filter((entry) => Boolean(entry?.on) && Boolean(entry?.instruction?.trim()))
    .map(
      (entry) =>
        `<custom_instruction title="${entry.title || "Instruction"}">\n${entry.instruction}\n</custom_instruction>`
    )
    .join("\n\n");
}

// Prime cache so reminder hooks can read backend mode data synchronously.
void backendSettingsService.getCustomInstructions("powerpoint").catch((error) => {
  console.warn("[App] Failed to warm custom instructions cache (powerpoint):", error);
});

configureAgentServices({
  hostType: "powerpoint",
  getLLMService: () => GlobalStateManager.getInstance().getLLMService() as any,
  getModelSettings: () => GlobalStateManager.getInstance().getModelSettings() as any,
  getJwtToken: () => GlobalStateManager.getInstance().getJwtToken(),
  getContextItems: (context: any, documentId?: string) => GlobalStateManager.getInstance().getContextItems(context, documentId) as any,
  contextResolver: getContextResolver() as any,
  reminderCollector: getReminderCollector() as any,
  messageBuilder: getMessageBuilder() as any,
  messagePreparer: getMessagePreparer() as any,
  logger: getSessionLogger() as any,
  checkpointManager: getCheckpointManager() as any,
  isMutatingToolName: (toolName: string) => isMutatingToolName(toolName),
  backendClient: backendClient as any,
  contextProviders: {
    getExternalFilesContext: () => {
      const files = externalFilesCache.getFiles();
      if (!files.length) return null;
      const content = externalFilesCache.formatAsContext();
      if (!content) return null;
      return { type: 'rag', title: 'External Files', content, source: 'RAG Index' };
    },
    getCustomInstructions: (category: string) => {
      const cached = backendSettingsService.getCachedCustomInstructions("powerpoint");
      const text =
        formatBackendInstructionsForCategory(cached, category) ||
        GlobalStateManager.getInstance().getActiveInstructionsText(category as any);
      return text ? { instructions: text, source: 'user' } : undefined;
    },
    getDocumentRegistry: () => {
      try { return getDeckRegistry(); } catch { return undefined; }
    },
    getPresentationContext: async () => {
      try {
        return await getReminderPresentationContext() as any;
      } catch {
        return undefined;
      }
    },
    getLiveDocumentId: async () => {
      try {
        const ctx = await getCurrentPresentationContext();
        return ctx?.documentId;
      } catch { return undefined; }
    },
    getDeckRegistryContext: (anchoredDocumentId?: string) => {
      return collectDeckRegistryContext(anchoredDocumentId);
    },
    getPresentationOverview: (documentId: string) => {
      return collectPresentationOverview(documentId);
    },
  },
});

const updateUserPreferenceSettings = async (updates: Record<string, unknown>) => {
  const globalState = GlobalStateManager.getInstance();
  const currentSettings = globalState.getUserSettings();
  await globalState.updateUserSettings({
    preferences: {
      ...(currentSettings.preferences || {}),
      ...updates,
    },
  });
};

const INDEXING_SETTINGS_UPDATED_EVENT = "carousel:indexing-settings-updated";

const buildSettingsDialogUrl = async (tabId?: string): Promise<string> => {
  console.log("[App] Building settings dialog URL", { tabId });
  const params = new URLSearchParams();
  if (tabId) {
    params.set("tab", tabId);
  }

  try {
    const registry = getDeckRegistry();
    let decks = registry.getAllDecks();
    if (decks.length === 0) {
      await registry.refresh();
      decks = registry.getAllDecks();
    }

    const currentDeck = decks.find((deck) => deck.isCurrent) || decks[0];
    if (currentDeck?.id) {
      params.set("documentId", currentDeck.id);
      params.set("deckName", currentDeck.title || "Current Presentation");
      console.log("[App] Resolved deck context for settings dialog", {
        documentId: currentDeck.id,
        deckName: currentDeck.title || "Current Presentation",
      });

      try {
        const storage = new LiveTrackingStorage();
        await storage.initialize();
        const deckState = await storage.getDeckState(currentDeck.id);
        if (typeof deckState?.indexingEnabled === "boolean") {
          params.set("deckOverrideEnabled", deckState.indexingEnabled ? "1" : "0");
          console.log("[App] Added deck override to settings dialog URL", {
            documentId: currentDeck.id,
            deckOverrideEnabled: deckState.indexingEnabled,
          });
        }
      } catch (storageError) {
        console.warn("[App] Could not read deck override while opening settings dialog:", storageError);
      }
    } else {
      console.warn("[App] No current deck found while building settings dialog URL");
    }
  } catch (error) {
    console.warn("[App] Could not resolve deck context for settings dialog URL:", error);
  }

  const query = params.toString();
  const url = `${window.location.origin}/settings.html${query ? `?${query}` : ""}`;
  console.log("[App] Settings dialog URL prepared", { url });
  return url;
};

// Helper function to handle settings updates from dialog
const handleSettingsUpdate = async (key: string, value: any) => {
  console.log('[App] Handling settings update:', key, value);
  
  // Import settingsService dynamically to avoid circular dependencies
  const { settingsService } = await import('../../services/settingsService');
  
  try {
    // Map shared Config keys to ppt-addin SettingsService
    if (key === 'carouselChatModel') {
      await settingsService.setChatModel(value);
    } else if (key === 'carouselChatReasoning') {
      await settingsService.setReasoningMode(value);
    } else if (key === 'editMode') {
      await settingsService.setEditMode(value);
    } else if (key === 'defaultBlackAndWhite') {
      await settingsService.setDefaultBlackAndWhite(value);
    } else if (key === 'defaultCollapseGroups') {
      await settingsService.setDefaultCollapseGroups(value);
    } else if (key === 'defaultClearComments') {
      await settingsService.setDefaultClearComments(value);
    } else if (key === 'isImageProcessingEnabled') {
      // Shared settings dialog uses this key for "Skip image processing in uploaded files".
      await updateUserPreferenceSettings({ skipPdfImages: Boolean(value) });
    } else if (key === 'isSlideIndexingEnabled') {
      await settingsService.setLiveSlideIndexingEnabled(Boolean(value));
      window.dispatchEvent(new CustomEvent(INDEXING_SETTINGS_UPDATED_EVENT));
    }
    // Add more mappings as needed
    
    console.log('[App] Settings updated successfully:', key);
  } catch (error) {
    console.error('[App] Error updating settings:', error);
  }
};

const handleDeckIndexingOverrideUpdate = async (documentId: string, enabled: boolean) => {
  if (!documentId || typeof enabled !== 'boolean') {
    console.warn('[App] Invalid deck indexing update payload:', { documentId, enabled });
    return;
  }

  console.log('[App] Handling deck indexing override update:', { documentId, enabled });

  try {
    const { LiveTrackingStorage } = await import('../../services/tracking/liveTrackingStorage');
    const storage = new LiveTrackingStorage();
    await storage.initialize();

    const updated = await storage.setDeckIndexingOverride(documentId, enabled);
    if (!updated) {
      // Deck may not have been initialized in LiveTrackingStorage yet.
      // Persist a minimal state so the override is durable and applied on first tracking cycle.
      const registry = getDeckRegistry();
      const deckInfo = registry.getDeckById(documentId);
      await storage.saveDeckState({
        documentId: documentId,
        deckName: deckInfo?.title || 'Current Presentation',
        slideIds: [],
        lastStructureCheck: Date.now(),
        isTracking: true,
        lastSelectionTime: Date.now(),
        indexingDeferred: true,
        indexingEnabled: enabled,
      });
      console.log('[App] Created deck state with indexing override for previously unseen deck:', {
        documentId,
        enabled,
      });
      window.dispatchEvent(new CustomEvent(INDEXING_SETTINGS_UPDATED_EVENT));
      return;
    }

    console.log('[App] Deck indexing override updated:', { documentId, enabled });
    window.dispatchEvent(new CustomEvent(INDEXING_SETTINGS_UPDATED_EVENT));
  } catch (error) {
    console.error('[App] Error updating deck indexing override:', error);
  }
};

const handlePromptLibraryDialogMessage = (message: unknown): void => {
  console.log('[App] Prompt library dialog message:', message);
  if (!message || typeof message !== 'object') {
    return;
  }

  const parsed = message as Record<string, any>;
  if (parsed.type === 'promptSaved' && parsed.prompt) {
    console.log('[App] Saving prompt with dialog ID:', parsed.prompt);
    try {
      const stored = localStorage.getItem('carousel_settings');
      const settings = stored ? JSON.parse(stored) : {};

      if (!settings.promptLibrary) {
        settings.promptLibrary = [];
      }

      settings.promptLibrary.push({
        id: parsed.prompt.id,
        title: parsed.prompt.title,
        content: parsed.prompt.content,
        host: "powerpoint",
      });

      localStorage.setItem('carousel_settings', JSON.stringify(settings));
      console.log('[App] Prompt saved successfully with ID:', parsed.prompt.id);

      window.dispatchEvent(new StorageEvent('storage', {
        key: 'carousel_settings',
        newValue: JSON.stringify(settings),
      }));
    } catch (error) {
      console.error('[App] Failed to save prompt:', error);
    }
  }
};

const taskpaneDialogOpeners = createTaskpaneAppDialogOpeners({
  isAuthenticated: () => authService.isAuthenticated(),
  logPrefix: '[App]',
  settingsDialogOptions: {
    height: 55,
    width: 40,
    displayInIframe: false,
  },
  promptLibraryDialogOptions: {
    height: 55,
    width: 40,
    displayInIframe: false,
  },
  resolveSettingsDialogUrl: buildSettingsDialogUrl,
  onSettingsOpenFailed: (error) => {
    console.error('[App] Failed to open settings dialog:', error);
  },
  onSettingsMessage: (message) => {
    console.log('[App] Settings dialog message:', message);
    if (!message || typeof message !== 'object') {
      return;
    }
    const parsed = message as Record<string, any>;
    if (parsed.type === 'settingsUpdated') {
      void handleSettingsUpdate(parsed.key, parsed.value);
    } else if (parsed.type === 'deckIndexingUpdated') {
      void handleDeckIndexingOverrideUpdate(parsed.documentId, parsed.enabled);
    }
  },
  onSettingsDialogEvent: (arg) => {
    if ('error' in arg) {
      console.log('[App] Settings dialog closed with error:', arg.error);
    } else {
      console.log('[App] Settings dialog closed');
    }
  },
  onPromptLibraryOpenFailed: (error) => {
    console.error('[App] Failed to open prompt library dialog:', error);
  },
  onPromptLibraryMessage: handlePromptLibraryDialogMessage,
  onPromptLibraryDialogEvent: (arg) => {
    if ('error' in arg) {
      console.log('[App] Prompt library dialog closed with error:', arg.error);
    } else {
      console.log('[App] Prompt library dialog closed');
    }
  },
});

// Helper function to open settings dialog popup
const openSettingsDialogPopup = (tabId?: string) => {
  taskpaneDialogOpeners.openSettingsDialog(tabId);
};

// Helper function to open prompt library dialog popup
const openPromptLibraryDialogPopup = (mode?: 'add' | 'view') => {
  taskpaneDialogOpeners.openPromptLibraryDialog(mode);
};

interface AppProps {
  title: string;
}

const useStyles = makeStyles({
  root: {
    width: "100%",
    height: "100vh",
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    flexDirection: "column",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
  },
});

// Inner component that has access to ConversationContext
const ChatPanelWithInit: React.FC = () => {
  const conversation = useConversation();
  const { setConversationId, addMessage, clearConversation, replaceMessages } = conversation;
  const [isInitializing, setIsInitializing] = React.useState(true);
  const [isReady, setIsReady] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const isReadyRef = React.useRef(false);
  const isInitializingRef = React.useRef(false);
  const conversationIdSetRef = React.useRef(false);

  // Model settings
  const { modelSettings } = useModelSettings();
  const [slideCountPill, setSlideCountPill] = React.useState<{
    ready: number;
    total: number;
    indexing?: number;
    retrying?: number;
    failed?: number;
    label?: string;
  }>({
    ready: 0,
    total: 0,
    indexing: 0,
    retrying: 0,
    failed: 0,
    label: 'Slides',
  });
  const [indexingUiState, setIndexingUiState] = React.useState<{
    globalEnabled: boolean;
    deckOverrideEnabled: boolean | null;
    effectiveEnabled: boolean;
  }>({
    globalEnabled: true,
    deckOverrideEnabled: null,
    effectiveEnabled: true,
  });
  const liveTrackingStorageRef = React.useRef<LiveTrackingStorage | null>(null);
  
  // Sync settings from shared Config on mount
  React.useEffect(() => {
    const syncSettingsFromSharedConfig = async () => {
      try {
        // Import shared config service
        const { getConfigService } = await import('@carousel-office/shared/services/configService');
        const configService = getConfigService();
        const config = configService.getConfig();
        
        // Import settings service
        const { settingsService } = await import('../../services/settingsService');
        
        // Sync model settings
        if (config.carouselChatModel) {
          await settingsService.updateModelSettings({
            chatModel: config.carouselChatModel as any,
            reasoningMode: config.carouselChatReasoning as any,
          });
        }
        
        // Sync Excel export settings
        if (config.editMode) {
          await settingsService.updateExcelExportSettings({
            editMode: config.editMode as any,
            defaultBlackAndWhite: config.defaultBlackAndWhite,
            defaultCollapseGroups: config.defaultCollapseGroups,
            defaultClearComments: config.defaultClearComments,
          });
        }

        // Sync shared "skip image processing in uploaded files" toggle to backend-facing preferences
        await updateUserPreferenceSettings({
          skipPdfImages: Boolean(config.isImageProcessingEnabled),
        });

        // Sync global slide indexing toggle
        await settingsService.setLiveSlideIndexingEnabled(
          config.isSlideIndexingEnabled !== false
        );
        
        console.log('[App] Settings synced from shared config');
      } catch (error) {
        console.error('[App] Error syncing settings:', error);
      }
    };
    
    syncSettingsFromSharedConfig();
  }, []); // Run once on mount
  
  // Prompts for popup
  const [prompts, setPrompts] = React.useState<SavedPrompt[]>([]);

  // Conversation history for popup
  const [conversations, setConversations] = React.useState<ConversationHistoryItem[]>([]);
  const [conversationsLoading, setConversationsLoading] = React.useState(false);

  // Fetch conversations
  const fetchConversationList = React.useCallback(async () => {
    setConversationsLoading(true);
    try {
      const convos = await fetchConversations();
      setConversations(convos);
      console.log(`[App] Fetched ${convos.length} conversations`);
    } catch (error) {
      console.error('[App] Error fetching conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  // Handle conversation selection
  const handleSelectConversation = React.useCallback(async (conversationId: string, isInMemory: boolean) => {
    console.log(`[App] Restoring conversation: ${conversationId} (in-memory: ${isInMemory})`);
    
    try {
      const restored = isInMemory 
        ? await restoreInMemoryConversation(conversationId)
        : await restorePersistedConversation(conversationId);
      
      if (restored) {
        console.log(`[App] Restored conversation ${restored.conversationId} with ${restored.messages.length} UI messages`);
        
        // Clear current conversation
        clearConversation();
        
        // Set the conversation ID
        setConversationId(restored.conversationId);
        
        // Small delay to ensure clearConversation completes
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Load all UI messages (they already have components for rich rendering)
        restored.messages.forEach((msg) => {
          addMessage(msg);
        });
        
        console.log(`[App] Loaded ${restored.messages.length} UI messages into ChatPanel`);
      } else {
        console.error('[App] Failed to restore conversation');
      }
    } catch (error) {
      console.error('[App] Error restoring conversation:', error);
    }
  }, [setConversationId, addMessage, clearConversation]);

  const handleRewriteConversationFromUserTurn = React.useCallback(async (userTurnOrdinal: number) => {
    const sourceConversationId = conversation.conversationId;
    if (!sourceConversationId) {
      return;
    }

    try {
      console.log(`[App] Rewriting conversation ${sourceConversationId} from user turn ${userTurnOrdinal}`);
      const activeConversation = conversations.find((entry) => entry.conversationId === sourceConversationId);
      const rewriteResult = await rewriteConversationFromUserTurn(
        sourceConversationId,
        userTurnOrdinal,
        conversation.messages as Message[],
        { title: activeConversation?.title }
      );
      replaceMessages(rewriteResult.messages);

      console.log(
        `[App] Rewrote conversation ${rewriteResult.conversationId} with ${rewriteResult.messages.length} UI messages`
      );
      await fetchConversationList();
    } catch (error) {
      console.error("[App] Failed to rewrite conversation from message:", error);
    }
  }, [
    conversation.conversationId,
    conversation.messages,
    conversations,
    fetchConversationList,
    replaceMessages,
  ]);

  // Fetch conversations on mount
  React.useEffect(() => {
    if (isReady) {
      fetchConversationList();
    }
  }, [isReady, fetchConversationList]);

  const contextUsagePercent = React.useMemo(() => {
    const conversationId = conversation.conversationId;
    if (!conversationId || conversation.messages.length === 0) {
      return null;
    }

    const globalState = GlobalStateManager.getInstance();
    const activeConversation = globalState.conversationRegistry[conversationId];
    if (!activeConversation?.agentRegistry) {
      return null;
    }

    const threadManager = activeConversation.agentRegistry.threadManager;
    const orchestratorThread = threadManager.getMostRecentThread(
      "livePowerPointOrchestrator",
      conversationId
    );
    if (!orchestratorThread) {
      return null;
    }

    const usagePercent = threadManager.getUsagePercent(orchestratorThread.threadId);
    return Number.isFinite(usagePercent) ? usagePercent : null;
  }, [conversation.conversationId, conversation.messages, conversation.isLoading]);

  // Auto-refresh conversation list when messages change (debounced)
  React.useEffect(() => {
    if (!isReady || !conversation.conversationId) {
      return undefined;
    }
    
    // Debounce to avoid too many refreshes during rapid message updates
    const timeoutId = setTimeout(() => {
      fetchConversationList();
    }, 2000); // Refresh 2 seconds after last message change
    
    return () => clearTimeout(timeoutId);
  }, [isReady, conversation.messages.length, conversation.conversationId, fetchConversationList]);

  // Fetch mention sections for @ mention modal (must be defined before early returns)
  const fetchMentionItems = React.useCallback(async (query?: string) => {
    return mentionFilesService.searchCategorized(query);
  }, []);

  // Load and subscribe to prompts
  React.useEffect(() => {
    // Initial load
    setPrompts(promptLibraryService.getPrompts());
    
    // Subscribe to changes
    const unsubscribe = promptLibraryService.subscribe((newPrompts) => {
      setPrompts(newPrompts);
    });
    
    return unsubscribe;
  }, []);

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let pollInterval: number | undefined;
    
    const initializeApp = async () => {
      // Prevent concurrent initialization
      if (isInitializingRef.current) {
        console.log('[App Component] Already initializing, skipping...');
        return;
      }

      try {
        isInitializingRef.current = true;
        setIsInitializing(true);
        
        // Initialize auth service - this will:
        // 1. Check localStorage for stored tokens
        // 2. Validate/refresh tokens if needed
        // 3. Return true if user is authenticated
        console.log('[App Component] Initializing auth service...');
        const authenticated = await authService.initialize();
        const token = authService.getAccessToken();
        
        console.log('[App Component] Auth initialized:', { 
          authenticated, 
          hasToken: !!token, 
          userEmail: authService.getCurrentUser()?.email 
        });
        
        if (authenticated && token) {
          setIsAuthenticated(true);
          
          if (!isReadyRef.current) {
            try {
              // Initialize message service with token available
              await initializeMessageService();
              isReadyRef.current = true;
              setIsReady(true);
              
              // Generate initial conversation ID only once
              if (!conversationIdSetRef.current) {
                const user = authService.getCurrentUser();
                if (!user?._id) {
                  throw new Error('User ID is required to create a conversation');
                }
                const initialConversationId = generateConversationId(user._id);
                setConversationId(initialConversationId);
                await createTaskpaneChatConversation(initialConversationId);
                conversationIdSetRef.current = true;
              }
            } catch (err) {
              console.error('[App Component] Failed to initialize message service:', err);
              isReadyRef.current = false;
              setIsReady(false);
            }
          }
        } else {
          console.log('[App Component] User not authenticated - waiting for login');
          setIsAuthenticated(false);
          isReadyRef.current = false;
          setIsReady(false);
        }
      } catch (err) {
        console.error('[App Component] Error during initialization:', err);
        setIsAuthenticated(false);
        isReadyRef.current = false;
        setIsReady(false);
      } finally {
        setIsInitializing(false);
        isInitializingRef.current = false;
      }
    };

    // Initial app initialization
    initializeApp();

    // Set up auth change listener for login/logout events
    // This only fires on actual auth state CHANGES (not on initial load)
    unsubscribe = authService.onAuthChange(async (isAuth, user) => {
      console.log('[App Component] Auth state changed:', { isAuth, userEmail: user?.email });
      
      // Ignore if we're already initializing
      if (isInitializingRef.current) {
        console.log('[App Component] Already initializing, ignoring auth change');
        return;
      }
      
      if (isAuth && !isReadyRef.current) {
        // User just logged in, initialize the app
        console.log('[App Component] User logged in, initializing app...');
        await initializeApp();
      } else if (!isAuth) {
        // User logged out
        console.log('[App Component] User logged out, resetting state');
        setIsAuthenticated(false);
        isReadyRef.current = false;
        setIsReady(false);
        conversationIdSetRef.current = false;
      }
    });

    // Poll periodically to catch auth changes from other contexts (e.g., login dialog)
    // This is a fallback in case the event doesn't fire (e.g., cross-context login)
    pollInterval = window.setInterval(async () => {
      // Only poll if not ready and not currently initializing
      if (!isReadyRef.current && !isInitializingRef.current) {
        // Check if auth state changed without reloading
        const wasAuthenticated = authService.isAuthenticated();
        if (!wasAuthenticated) {
          // Reload from storage to catch updates from login dialog
          await authService.reloadFromStorage();
          const nowAuthenticated = authService.isAuthenticated();
          if (nowAuthenticated && !wasAuthenticated) {
            console.log('[App Component] Auth detected via polling, initializing...');
            await initializeApp();
          }
        }
      }
    }, 2000); // Check every 2 seconds if not ready

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      if (pollInterval) {
        window.clearInterval(pollInterval);
      }
    };
  }, []); // Stable dependencies - using refs to track state

  // Keep header slide counts in sync with DeckRegistry
  React.useEffect(() => {
    let mounted = true;
    const registry = getDeckRegistry();

    const updateSlideCountPill = async () => {
      try {
        let decks = registry.getAllDecks();

        // Lazy refresh if registry has not been populated yet.
        if (decks.length === 0) {
          await registry.refresh();
          decks = registry.getAllDecks();
        }

        const currentDeck = decks.find((deck) => deck.isCurrent) || decks[0];
        const globalIndexingEnabled = settingsService.getLiveSlideIndexingEnabled();
        let deckOverrideEnabled: boolean | null = null;

        if (currentDeck?.id) {
          if (!liveTrackingStorageRef.current) {
            liveTrackingStorageRef.current = new LiveTrackingStorage();
            await liveTrackingStorageRef.current.initialize();
          }
          const deckState = await liveTrackingStorageRef.current.getDeckState(currentDeck.id);
          if (typeof deckState?.indexingEnabled === "boolean") {
            deckOverrideEnabled = deckState.indexingEnabled;
          }
        }

        const effectiveIndexingEnabled =
          typeof deckOverrideEnabled === "boolean" ? deckOverrideEnabled : globalIndexingEnabled;

        if (!mounted) return;

        if (currentDeck) {
          setSlideCountPill({
            ready: currentDeck.slidesReady,
            total: currentDeck.totalSlides,
            indexing: currentDeck.slidesIndexing || 0,
            retrying: currentDeck.slidesRetrying || 0,
            failed: currentDeck.slidesFailed || 0,
            label: 'Slides',
          });
        } else {
          setSlideCountPill({
            ready: 0,
            total: 0,
            indexing: 0,
            retrying: 0,
            failed: 0,
            label: 'Slides',
          });
        }

        setIndexingUiState({
          globalEnabled: globalIndexingEnabled,
          deckOverrideEnabled,
          effectiveEnabled: effectiveIndexingEnabled,
        });
      } catch (error) {
        if (!mounted) return;
        console.warn('[App] Failed to update slide count pill:', error);
      }
    };

    void updateSlideCountPill();
    const unsubscribe = registry.subscribe(() => {
      if (!mounted) return;
      void updateSlideCountPill();
    });

    const intervalId = window.setInterval(() => {
      void updateSlideCountPill();
    }, 5000);

    const onIndexingSettingsUpdated = () => {
      if (!mounted) return;
      void updateSlideCountPill();
    };
    window.addEventListener(INDEXING_SETTINGS_UPDATED_EVENT, onIndexingSettingsUpdated as EventListener);

    return () => {
      mounted = false;
      unsubscribe();
      window.clearInterval(intervalId);
      window.removeEventListener(INDEXING_SETTINGS_UPDATED_EVENT, onIndexingSettingsUpdated as EventListener);
    };
  }, []);

  // Show loading state while initializing
  if (isInitializing) {
    return <InitializingScreen />;
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated && !isReady) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md px-4">
          <div className="text-gray-700 text-lg font-semibold mb-2">
            Please Sign In
          </div>
          <div className="text-gray-600 text-sm mb-4">
            To use this feature, please sign in using the Login button in the ribbon.
          </div>
          <div className="text-gray-500 text-xs">
            Click the "Login" button in the PowerPoint ribbon to get started.
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ChatPanel
        onOpenSettings={openSettingsDialogPopup}
        onOpenPromptLibraryDialog={openPromptLibraryDialogPopup}
        conversations={conversations}
        onSelectConversation={handleSelectConversation}
        onRefreshConversations={fetchConversationList}
        onRewriteConversationFromUserTurn={handleRewriteConversationFromUserTurn}
        conversationsLoading={conversationsLoading}
        modelSettings={{
          chatModel: modelSettings.chatModel,
          reasoningMode: modelSettings.reasoningMode,
        }}
        contextUsagePercent={contextUsagePercent}
        slideCountPill={slideCountPill}
        indexingEnabled={indexingUiState.effectiveEnabled}
        prompts={prompts.map(p => ({
          id: p.id,
          title: p.title,
          content: p.content,
        }))}
        fetchMentionItems={fetchMentionItems}
      />
    </>
  );
};

const App: React.FC<AppProps> = ({ title }) => {
  const styles = useStyles();

  console.log('[App Component] Rendering with title:', title);

  React.useEffect(() => {
    console.log('[App Component] Mounted');
    return () => {
      console.log('[App Component] Unmounting');
    };
  }, []);

  // Dev mode state management - listen for changes from admin panel
  React.useEffect(() => {
    // Initialize from localStorage
    const savedDevMode = localStorage.getItem('carousel-dev-mode') === 'true';
    console.log('[App Component] Initializing dev mode from localStorage:', savedDevMode);
    setDevModeEnabled(savedDevMode);

    // Listen for changes from admin panel (via shared runtime)
    const handleDevModeChange = (event: CustomEvent<{ enabled: boolean }>) => {
      console.log('[App Component] Dev mode changed via event:', event.detail.enabled);
      setDevModeEnabled(event.detail.enabled);
    };

    window.addEventListener('carousel-dev-mode-changed', handleDevModeChange as EventListener);
    
    return () => {
      window.removeEventListener('carousel-dev-mode-changed', handleDevModeChange as EventListener);
    };
  }, []);

  return (
    <div className={styles.root}>
      <ConversationProvider>
        <ChatPanelWithInit />
      </ConversationProvider>
      <DebugPanel />
      <ToolTestPanel />
    </div>
  );
};

export default App;
